export interface ShipmentSupplyRequestLine {
  meal_service: string;
  description: string;
  qty: number;
  uom?: string | null;
}

export interface ShipmentSupplyRequestForAllocation {
  id: string;
  status: string;
  archived_at?: string | null;
  lines?: readonly ShipmentSupplyRequestLine[] | null;
}

export interface ShipmentSupplyAllocationLineV1 {
  request_line_index: number;
  description: string;
  qty: number;
  uom: string | null;
}

export interface ShipmentSupplyAllocationV1 {
  request_id: string;
  meal_service: string;
  lines: ShipmentSupplyAllocationLineV1[];
}

export interface ShipmentOrderBackedLineV1 {
  production_id: string | null;
  description: string;
  batch_lot: string;
  qty: string | number;
  uom: string;
}

function normalizedLineText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function numericQty(value: string | number): number | null {
  const parsed = typeof value === 'number'
    ? value
    : Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Proves that every expected order-backed dish still has one exact DDT row.
 * Extra free rows are allowed, but they cannot impersonate an order row
 * because production_id, normalized description, quantity, UOM and lot must
 * all match.
 */
export function shipmentLinesMatchOrderSnapshot(
  current: readonly ShipmentOrderBackedLineV1[],
  expected: readonly ShipmentOrderBackedLineV1[],
): boolean {
  const consumed = new Set<number>();
  return expected.every((wanted) => {
    const wantedQty = numericQty(wanted.qty);
    if (wantedQty == null || !wanted.production_id) return false;
    const index = current.findIndex((candidate, candidateIndex) => (
      !consumed.has(candidateIndex)
      && candidate.production_id === wanted.production_id
      && normalizedLineText(candidate.description)
          === normalizedLineText(wanted.description)
      && numericQty(candidate.qty) === wantedQty
      && candidate.uom.trim().toLowerCase() === wanted.uom.trim().toLowerCase()
      && (
        !wanted.batch_lot.trim()
        || candidate.batch_lot.trim() === wanted.batch_lot.trim()
      )
    ));
    if (index < 0) return false;
    consumed.add(index);
    return true;
  });
}

export function clearShipmentSupplyAllocations<
  T extends { supply_allocations_v1: ShipmentSupplyAllocationV1[] },
>(value: T): T {
  if (value.supply_allocations_v1.length === 0) return value;
  return { ...value, supply_allocations_v1: [] };
}

/**
 * Freeze the exact accepted request rows covered by an order-backed DDT.
 *
 * Allocation is deliberately opt-in: callers invoke this only from
 * "Compila dagli ordini". A free/manual DDT therefore cannot accidentally
 * fulfill every request for the same destination and day.
 */
export function buildShipmentSupplyAllocations(
  requests: readonly ShipmentSupplyRequestForAllocation[],
  mealServiceInput: string,
): ShipmentSupplyAllocationV1[] {
  const mealService = mealServiceInput.trim().toLowerCase();
  if (!mealService) return [];

  const allocations = new Map<string, ShipmentSupplyAllocationV1>();
  for (const request of requests) {
    if (
      request.archived_at
      || (request.status !== 'accepted' && request.status !== 'in_production')
      || allocations.has(request.id)
    ) {
      continue;
    }
    const lines = (request.lines ?? []).flatMap((line, index) => {
      if (line.meal_service.trim().toLowerCase() !== mealService) return [];
      const qty = Number(line.qty);
      if (!Number.isFinite(qty) || qty <= 0) return [];
      return [{
        request_line_index: index,
        description: line.description,
        qty,
        uom: line.uom?.trim() || null,
      }];
    });
    if (lines.length === 0) continue;
    allocations.set(request.id, {
      request_id: request.id,
      meal_service: mealService,
      lines,
    });
  }

  return [...allocations.values()].sort((left, right) => (
    left.request_id.localeCompare(right.request_id)
  ));
}
